import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Backtest.css';
import AiCommentary from './AiCommentary';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Backtest = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [period, setPeriod] = useState('3y');
  const [symbolPreset, setSymbolPreset] = useState('qqq_spy');
  const [customSymbol1, setCustomSymbol1] = useState('');
  const [customSymbol2, setCustomSymbol2] = useState('');
  const [cashProxy, setCashProxy] = useState('');

  const periodOptions = {
    '3y': { start: '2023-04-17', end: '2026-04-17', label: '3 Years' },
    '5y': { start: '2021-04-17', end: '2026-04-17', label: '5 Years' },
    '10y': { start: '2016-04-17', end: '2026-04-17', label: '10 Years' },
  };

  const symbolPresets = {
    'qqq_spy': { symbol1: 'QQQ', symbol2: 'SPY', label: 'QQQ / SPY' },
    'custom': { symbol1: '', symbol2: '', label: 'Custom' },
  };

  const getActiveSymbols = (preset = symbolPreset) => {
    if (preset === 'custom') {
      return { symbol1: customSymbol1.toUpperCase().trim(), symbol2: customSymbol2.toUpperCase().trim() };
    }
    return symbolPresets[preset];
  };

  const runBacktest = async (force = false, selectedPeriod = period, selectedPreset = symbolPreset) => {
    setLoading(true);
    setError(null);
    const p = periodOptions[selectedPeriod];
    const s = selectedPreset === 'custom' ? { symbol1: customSymbol1.toUpperCase().trim(), symbol2: customSymbol2.toUpperCase().trim() } : symbolPresets[selectedPreset];
    if (!s.symbol1 || !s.symbol2) {
      setError('Please enter both symbols');
      setLoading(false);
      return;
    }
    const proxyParam = cashProxy.trim() ? `&cash_proxy=${cashProxy.trim().toUpperCase()}` : '';
    try {
      const res = await fetch(`${API_URL}/api/backtest/run?force=${force}&start_date=${p.start}&end_date=${p.end}&symbol1=${s.symbol1}&symbol2=${s.symbol2}${proxyParam}`);
      const data = await res.json();
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError(data.message || 'Backtest failed');
      }
    } catch (err) {
      setError('Connection error - please try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runBacktest(); }, []);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    runBacktest(true, newPeriod, symbolPreset);
  };

  const handlePresetChange = (newPreset) => {
    setSymbolPreset(newPreset);
    if (newPreset !== 'custom') {
      runBacktest(true, period, newPreset);
    }
  };

  const handleCustomRun = () => {
    setSymbolPreset('custom');
    runBacktest(true, period, 'custom');
  };

  const MetricCard = ({ label, value, subValue, color }) => (
    <div className="bt-metric-card">
      <span className="bt-metric-label">{label}</span>
      <span className="bt-metric-value" style={color ? { color } : {}}>{value}</span>
      {subValue && <span className="bt-metric-sub">{subValue}</span>}
    </div>
  );

  const formatCurrency = (val) => `$${val?.toLocaleString() || 0}`;

  return (
    <div className="bt-container" data-testid="backtest-page">
      {/* Header */}
      <div className="bt-header">
        <a href="/explore" className="bt-back-link">Back to Dashboard</a>
        <h1>Strategy Backtest</h1>
        <p className="bt-subtitle">
          {results?.symbols ? `${results.symbols.symbol1} / ${results.symbols.symbol2} / ${results.symbols.cash_proxy} Rotation Strategy` : 'QQQ / SPY / Cash Rotation Strategy'}
        </p>
        <p className="bt-description">
          Rotates between QQQ, SPY, and Cash based on 200-day MA health, 50/200 MA crossover, and 6-month momentum.
        </p>
      </div>

      {/* Symbol Selector + Period Selector + Run Button */}
      <div className="bt-actions">
        <div className="bt-symbol-selector" data-testid="backtest-symbol-selector">
          <span className="bt-selector-label">Strategy:</span>
          {Object.entries(symbolPresets).filter(([k]) => k !== 'custom').map(([key, val]) => (
            <button key={key} onClick={() => handlePresetChange(key)} className={`bt-period-btn ${symbolPreset === key ? 'active' : ''}`} disabled={loading}>
              {val.label}
            </button>
          ))}
        </div>
        <div className="bt-custom-inputs" data-testid="backtest-custom-symbols">
          <span className="bt-selector-label">Custom:</span>
          <input type="text" value={customSymbol1} onChange={e => setCustomSymbol1(e.target.value)} placeholder="Symbol 1 (e.g. QQQ)" className="bt-symbol-input" data-testid="backtest-symbol1-input" />
          <span className="bt-vs-label">/</span>
          <input type="text" value={customSymbol2} onChange={e => setCustomSymbol2(e.target.value)} placeholder="Symbol 2 (e.g. SPY)" className="bt-symbol-input" data-testid="backtest-symbol2-input" />
          <button onClick={handleCustomRun} disabled={loading || !customSymbol1 || !customSymbol2} className="bt-period-btn active" data-testid="backtest-custom-run">
            Run
          </button>
        </div>
        <div className="bt-custom-inputs" data-testid="backtest-cash-proxy">
          <span className="bt-selector-label">Cash Proxy:</span>
          <input type="text" value={cashProxy} onChange={e => setCashProxy(e.target.value)} placeholder="e.g. SGOV (leave blank for 0%)" className="bt-symbol-input bt-cash-input" data-testid="backtest-cash-proxy-input" />
        </div>
        <div className="bt-period-selector" data-testid="backtest-period-selector">
          <span className="bt-selector-label">Period:</span>
          {Object.entries(periodOptions).map(([key, val]) => (
            <button key={key} onClick={() => handlePeriodChange(key)} className={`bt-period-btn ${period === key ? 'active' : ''}`} disabled={loading}>
              {val.label}
            </button>
          ))}
        </div>
        <button onClick={() => runBacktest(true)} disabled={loading} className="bt-run-btn" data-testid="backtest-run-btn">
          {loading ? 'Running Backtest...' : 'Re-run Backtest'}
        </button>
      </div>

      {loading && (
        <div className="bt-loading" data-testid="backtest-loading">
          <div className="bt-spinner"></div>
          <p>Running 36-month backtest against live Alpha Vantage data...</p>
          <p className="bt-loading-sub">This may take a moment for the initial data fetch.</p>
        </div>
      )}

      {error && <div className="bt-error" data-testid="backtest-error">{error}</div>}

      {results && !loading && (
        <>
          {/* Period Info */}
          <div className="bt-period" data-testid="backtest-period">
            Backtest Period: <strong>{results.backtest_period.start}</strong> to <strong>{results.backtest_period.end}</strong>
            {' '}({results.backtest_period.trading_days} trading days, {results.backtest_period.years} years)
          </div>

          {/* Strategy Metrics */}
          <div className="bt-metrics-section">
            <h2>Strategy Performance</h2>
            <div className="bt-metrics-row" data-testid="backtest-strategy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.strategy_metrics.ending_value)} subValue={`from ${formatCurrency(results.strategy_metrics.starting_value)}`} />
              <MetricCard label="Total Return" value={`${results.strategy_metrics.total_return_pct > 0 ? '+' : ''}${results.strategy_metrics.total_return_pct}%`} color={results.strategy_metrics.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.strategy_metrics.cagr}%`} color={results.strategy_metrics.cagr >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="Max Drawdown" value={`-${results.strategy_metrics.max_drawdown}%`} color="#f59e0b" />
              <MetricCard label="Sharpe Ratio" value={results.strategy_metrics.sharpe_ratio} color={results.strategy_metrics.sharpe_ratio >= 1 ? '#22c55e' : '#f59e0b'} />
              <MetricCard label="Total Trades" value={results.strategy_metrics.total_trades} />
            </div>
          </div>

          {/* SPY Benchmark */}
          <div className="bt-metrics-section benchmark">
            <h2>SPY Buy-and-Hold Benchmark</h2>
            <div className="bt-metrics-row" data-testid="backtest-spy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.spy_benchmark.ending_value)} subValue="from $100,000" />
              <MetricCard label="Total Return" value={`${results.spy_benchmark.total_return_pct > 0 ? '+' : ''}${results.spy_benchmark.total_return_pct}%`} color={results.spy_benchmark.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.spy_benchmark.cagr}%`} />
              <MetricCard label="Max Drawdown" value={`-${results.spy_benchmark.max_drawdown}%`} color="#f59e0b" />
            </div>
          </div>

          {/* Outperformance Summary */}
          {(() => {
            const diff = results.strategy_metrics.total_return_pct - results.spy_benchmark.total_return_pct;
            const outperformed = diff > 0;
            return (
              <div className={`bt-outperformance ${outperformed ? 'positive' : 'negative'}`} data-testid="backtest-outperformance">
                Strategy {outperformed ? 'outperformed' : 'underperformed'} SPY by{' '}
                <strong>{Math.abs(diff).toFixed(1)} percentage points</strong>
                {' '}with{' '}
                <strong>{(results.spy_benchmark.max_drawdown - results.strategy_metrics.max_drawdown).toFixed(1)}%</strong>
                {' '}{results.strategy_metrics.max_drawdown < results.spy_benchmark.max_drawdown ? 'less' : 'more'} drawdown
              </div>
            );
          })()}

          {/* AI Commentary */}
          <AiCommentary which="1" period="default" key={`bt1-${period}-${symbolPreset}`} />

          {/* Equity Curve */}
          <div className="bt-chart-section" data-testid="backtest-chart">
            <h2>Equity Curve: Strategy vs SPY Buy-and-Hold</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={results.equity_curve} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} interval={Math.floor(results.equity_curve.length / 12)} />
                <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }}
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Legend />
                <Line type="monotone" dataKey="portfolio_return" name="Strategy" stroke="#8b5cf6" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="spy_return" name="SPY Buy & Hold" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="5 5" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Trade Log */}
          <div className="bt-trades-section" data-testid="backtest-trades">
            <div className="bt-trades-header">
              <h2>Position Changes ({results.total_trades_list_count} total trades)</h2>
              {results.trades.length > 10 && (
                <button onClick={() => setShowAllTrades(!showAllTrades)} className="bt-toggle-btn">
                  {showAllTrades ? 'Show Less' : 'Show All'}
                </button>
              )}
            </div>
            <div className="bt-table-wrapper">
              <table className="bt-trades-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Action</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Portfolio Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllTrades ? results.trades : results.trades.slice(-15)).map((trade, idx) => (
                    <tr key={idx}>
                      <td>{trade.trade_number}</td>
                      <td>{trade.date}</td>
                      <td>{trade.action}</td>
                      <td><span className={`bt-position-tag ${trade.from.toLowerCase()}`}>{trade.from}</span></td>
                      <td><span className={`bt-position-tag ${trade.to.toLowerCase()}`}>{trade.to}</span></td>
                      <td>${trade.portfolio_value?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Backtest;
